import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import { useTypography } from '../../context/TypographyContext';
import { debounce } from 'lodash';
import tokens from '../../theme/tokens';
import { useLanguage } from '../../localization';

function TypographySettings() {
  const { typographySettings, updateTypographySettings, resetToDesignDefaults } = useTypography();
  const isMobile = useMediaQuery(`(max-width:${tokens.spacing[24]})`);
  const [localSettings, setLocalSettings] = useState({...typographySettings});
  const { t } = useLanguage();

  // Update local state when context changes
  useEffect(() => {
    setLocalSettings(prev => ({
        ...typographySettings,
        cvTitle: { ...typographySettings.cvTitle, ...(prev.cvTitle || {}) },
        sectionTitle: { ...typographySettings.sectionTitle, ...(prev.sectionTitle || {}) },
        body: { ...typographySettings.body, ...(prev.body || {}) },
        smallText: { ...typographySettings.smallText, ...(prev.smallText || {}) },
    }));
  }, [typographySettings]);

  // Define NEW sections
  const textSections = [
    { id: 'cvTitle', title: t('settingsPanel.typography.sections.cvTitle') },
    { id: 'sectionTitle', title: t('settingsPanel.typography.sections.sectionTitle') },
    { id: 'body', title: t('settingsPanel.typography.sections.body') },
    { id: 'smallText', title: t('settingsPanel.typography.sections.smallText') },
  ];

  // Create debounced update functions for each property
  const debouncedUpdateFontSize = debounce((section, value) => {
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      updateTypographySettings({
        [section]: {
          ...localSettings[section],
          fontSize: numericValue
        }
      });
    }
  }, 300);

  const debouncedUpdateLineHeight = debounce((section, value) => {
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      updateTypographySettings({
        [section]: {
          ...localSettings[section],
          lineHeight: numericValue
        }
      });
    }
  }, 300);

  // Handle font size changes with debouncing
  const handleFontSizeChange = (section, event) => {
    const value = event.target.value;
    setLocalSettings(prev => {
      const newSettings = {...prev};
      if (!newSettings[section]) {
          newSettings[section] = {};
      }
      newSettings[section] = {
        ...newSettings[section],
        fontSize: value
      };
      return newSettings;
    });
    debouncedUpdateFontSize(section, value);
  };

  const handleLineHeightChange = (section, event) => {
    const value = event.target.value;
    setLocalSettings(prev => {
      const newSettings = {...prev};
       if (!newSettings[section]) {
          newSettings[section] = {};
      }
      newSettings[section] = {
        ...newSettings[section],
        lineHeight: value
      };
      return newSettings;
    });
    debouncedUpdateLineHeight(section, value);
  };

  // Font family doesn't need debouncing (instant change is fine)
  const handleFontFamilyChange = (section, event) => {
    const value = event.target.value;
    const updatedLocalSection = {
        ...(localSettings[section] || {}),
        fontFamily: value
    };
    setLocalSettings(prev => ({
        ...prev,
        [section]: updatedLocalSection
    }));
    updateTypographySettings({
      [section]: updatedLocalSection
    });
  };

  // Handle reset to design defaults
  const handleResetToDefaults = () => {
    resetToDesignDefaults();
  };

  // Define font family options
  const fontFamilies = [
    "'Arial', sans-serif",
    "'Helvetica', sans-serif",
    "'Roboto', sans-serif",
    "'Times New Roman', serif",
    "'Georgia', serif",
    "'Courier New', monospace",
    "'Open Sans', sans-serif",
    "'Lato', sans-serif",
    "'Montserrat', sans-serif",
    "'Product Sans', 'Roboto', sans-serif",
    "'SF Pro Text', 'Helvetica', sans-serif",
  ];

  // Helper function to render a section
  const renderSection = (id, title, content) => {
    return isMobile ? (
      <Accordion key={id} disableGutters elevation={0} sx={{
        mb: tokens.spacing[2],
        border: `1px solid ${tokens.colors.borders.default}`,
        borderRadius: tokens.radius.sm,
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: 0,
            marginBottom: tokens.spacing[2],
        }
      }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${id}-content`}
          id={`${id}-header`}
          sx={{
            minHeight: 'auto',
            padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
            '&.Mui-expanded': {
                minHeight: 'auto',
            },
            '& .MuiAccordionSummary-content': {
                margin: `${tokens.spacing[1]} 0`,
                '&.Mui-expanded': {
                    margin: `${tokens.spacing[1]} 0`,
                }
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: tokens.typography.fontWeights.medium, fontSize: tokens.typography.fontSizes.sm, color: tokens.colors.text.primary }}>{title}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: `${tokens.spacing[1]} ${tokens.spacing[3]} ${tokens.spacing[3]}` }}>
          {content}
        </AccordionDetails>
      </Accordion>
    ) : (
      <Box key={id} sx={{ mb: tokens.spacing[6] }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: tokens.typography.fontWeights.medium, color: tokens.colors.text.primary, fontSize: tokens.typography.fontSizes.lg }}>
          {title}
        </Typography>
        {content}
      </Box>
    );
  };

  // Render global section
  const renderGlobalSection = () => {
    return null; // We've removed global settings for simplicity
  };

  // Main render function for the content
  const renderContent = () => {
    return (
      <Box sx={{
        display: 'flex',
        gap: tokens.spacing[4],
        flexDirection: 'column',
        width: '100%',
      }}>
        {/* Reset to Design Defaults Button */}
        <Button
          startIcon={<RestoreIcon />}
          onClick={handleResetToDefaults}
          variant="contained"
          size="small"
          sx={{
            mb: tokens.spacing[4],
            backgroundColor: tokens.colors.secondary.light,
            color: tokens.colors.secondary.contrast,
            padding: `${tokens.spacing[1.5]} ${tokens.spacing[3]}`,
            borderRadius: tokens.radius.sm,
            alignSelf: 'flex-start',
            '&:hover': {
              backgroundColor: tokens.colors.secondary.main,
            }
          }}
        >
          {t('settingsPanel.typography.reset')}
        </Button>

        {/* Global Typography Settings */}
        {renderGlobalSection()}

        {/* Section-specific Typography Settings - Map over NEW sections */}
        {textSections.map(({ id: section, title }) => {
          // Use localSettings for displaying values in inputs
          const currentSectionSettings = localSettings[section] || {};
          // Provide default empty strings if values are null/undefined
          const currentFontSize = currentSectionSettings.fontSize ?? '';
          const currentLineHeight = currentSectionSettings.lineHeight ?? '';
          const currentFontFamily = currentSectionSettings.fontFamily ?? '';

          // Define min/max for font size based on section if needed
          let minFontSize = 6;
          let maxFontSize = 24;
          if (section === 'cvTitle') {
              maxFontSize = 48;
          } else if (section === 'sectionTitle') {
              maxFontSize = 36;
          }

          const sectionContent = (
            <Grid container spacing={isMobile ? tokens.spacing[2] : tokens.spacing[4]} alignItems="flex-start">
              {/* Font Size Input */}
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <TextField
                    label={t('settingsPanel.typography.fields.size')}
                    type="number"
                    name={`${section}-fontSize`}
                    value={currentFontSize}
                    onChange={(e) => handleFontSizeChange(section, e)}
                    size="small"
                    InputProps={{ inputProps: { min: minFontSize, max: maxFontSize, step: 0.5 } }}
                    key={`${section}-fontSize-${currentFontSize}`}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>

              {/* Line Height (Spacing) Input - Now always shown */}
               <Grid item xs={12} sm={4} md={3}>
                 <FormControl fullWidth size="small">
                   <TextField
                     label={t('settingsPanel.typography.fields.spacing')}
                     type="number"
                     name={`${section}-lineHeight`}
                     value={currentLineHeight}
                     onChange={(e) => handleLineHeightChange(section, e)}
                     size="small"
                     InputProps={{ inputProps: { min: 0.8, max: 3, step: 0.1 } }}
                     key={`${section}-lineHeight-${currentLineHeight}`}
                     variant="outlined"
                   />
                 </FormControl>
               </Grid>

              {/* Font Family Input */}
              {/* Adjust grid size since Line Height is always present */}
              <Grid item xs={12} sm={4} md={6}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id={`${section}-font-family-label`}>{t('settingsPanel.typography.fields.fontFamily')}</InputLabel>
                  <Select
                    key={`${section}-font-family-${currentFontFamily}`}
                    labelId={`${section}-font-family-label`}
                    id={`${section}-font-family-select`}
                    value={currentFontFamily}
                    label={t('settingsPanel.typography.fields.fontFamily')}
                    onChange={(e) => handleFontFamilyChange(section, e)}
                  >
                    {fontFamilies.map((font) => (
                      <MenuItem
                        key={font}
                        value={font}
                        sx={{ fontFamily: font, fontSize: tokens.typography.fontSizes.sm }}
                      >
                        {font.split(',')[0].replace(/'/g, '')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          );

          // Use the title from the textSections array
          return renderSection(
            section,
            title,
            sectionContent
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{
      padding: isMobile ? tokens.spacing[4] : tokens.spacing[6],
      overflowY: 'auto',
      height: '100%',
      backgroundColor: tokens.colors.background.default,
    }}>
      {renderContent()}
    </Box>
  );
}

export default TypographySettings;